import { forwardRef, useEffect, useLayoutEffect, useRef } from "react";
import Quill from "quill";
import { uploadImage } from "../api/helper/upload-image";

const Editor = forwardRef(
  (
    { readOnly = false, defaultValue, onTextChange, onSelectionChange },
    ref
  ) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    const imageHandler = function (value) {
      if (value) {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (event) => {
          const file = event.target.files[0];
          const formData = new FormData();
          formData.append("file", file);
          const { data: image } = await uploadImage(formData);

          if (!image?.publicUrl) {
            alert("이미지 업로드 실패");
            return;
          }

          const range = this.quill.getSelection();
          this.quill.insertEmbed(range.index, "image", image?.publicUrl);
        };
        input.click();
      }
    };

    const initQuill = ({ container }) => {
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement("div")
      );
      const quill = new Quill(editorContainer, {
        theme: "snow",
        modules: {
          toolbar: {
            container: [
              // [{ font: [] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],

              ["bold", "italic", "underline", "strike"], // toggled buttons
              ["blockquote", "code-block"],
              ["link", "image", "video", "formula"],

              [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
              [{ script: "sub" }, { script: "super" }], // superscript/subscript
              [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
              // [{ direction: 'rtl' }], // text direction

              [{ color: [] }, { background: [] }], // dropdown with defaults from theme
              [{ align: [] }],

              ["clean"], // remove formatting button
            ],
            handlers: {
              image: imageHandler,
            },
          },
        },
      });

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current);
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      return quill;
    };

    useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const quill = initQuill({ container });
      ref.current = quill;

      return () => {
        ref.current = null;
        container.innerHTML = "";
      };
    }, [ref, containerRef]);

    useEffect(() => {
      ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    return <div ref={containerRef} className="quill-editor"></div>;
  }
);

Editor.displayName = "Editor";

export default Editor;
